/**
 * Blog videos, from hung-hola blocks/blog/view.js: an endless column moved only by the prev / next buttons (no
 * autoplay, owner 2026-10-20), vertical from 1025px (the arrows point up / down) and sideways below (left / right).
 * Each press shows one whole video, snapped. Every video sits at a multiple of one set length (all videos + gaps) and
 * gsap.utils.wrap keeps the shift inside one set length, so each copy takes the place of the one next to it without a
 * jump; at rest the real videos sit where they are in the static column (the first frame matches it).
 * - Every move is a spring (SPRING, owner: no fixed duration or ease): it pulls the column to its video, keeps the speed
 *   it already has (a second press or a throw carries on, never restarts) and settles with one small overshoot.
 * - Prev / next move by one video + gap; the loop has no ends, so the buttons never disable.
 * - Drag (mouse or touch) on the panel, its buttons aside, moves the column with the pointer; on release the spring takes
 *   it to the nearest video from the release speed, carried on by the throw (THROW ms of that speed). A drag is never
 *   a click (play).
 * - Videos are cloned (aria-hidden, their buttons out of the tab order) when two sets do not cover the column.
 * - ?still and reduced motion: the buttons jump one video at once.
 * - Play, or a click anywhere on the card, swaps the card's poster for YouTube's player (youtube-nocookie, autoplay);
 *   from then on YouTube's own controls pause it. Prev / next play the video they bring in, with sound, once the
 *   column settles; a browser that blocks sound (iOS Safari) gets it muted. One card plays at a time; moving the column or leaving the screen
 *   pauses it (the iframe API's postMessage commands). Delegated on the column, so the copies play too.
 * GSAP: Nectar's, on window (block.json viewScript "gsap"). The fade-in is the shared one (data-px-fade-in).
 */
import { isStill } from '../../assets/js/motion';

// Spring per unit mass: damping / (2 √stiffness) ≈ 0.8, one small overshoot, settled in ~0.7 s
const SPRING = { stiffness: 170, damping: 21, rest: 0.1 }; // rest: px and px/s under which it stops
const VERTICAL = '(width > 1024px)'; // the layout's switch (render.php: tablet-h:)
const DRAG_START = 5; // px the pointer moves before a press is a drag
const THROW = 150; // ms of the release speed added to where a drag settles
const BLOCKED_AFTER = 2500; // ms after the player loads: still not playing = the browser blocked sound, start muted

document.querySelectorAll<HTMLElement>('[data-px-block="blog"]').forEach((wrapper) => {
	const gsap = window.gsap;
	const track = wrapper.querySelector<HTMLElement>('[data-blog-track]');
	const videos = [...wrapper.querySelectorAll<HTMLElement>('[data-blog-video]')];
	if (!gsap || !track || !videos.length) {
		return;
	}
	const prev = wrapper.querySelector<HTMLButtonElement>('[data-blog-prev]');
	const next = wrapper.querySelector<HTMLButtonElement>('[data-blog-next]');

	const vertical = window.matchMedia(VERTICAL);
	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
	let axis: 'x' | 'y' = 'y';
	const offset = { value: 0 }; // prev / next shift on top of the loop position
	const items = [...videos]; // videos + their aria-hidden copies
	let step = 0; // one video + gap
	let setLength = 0;
	let wrap: ((value: number) => number) | null = null;
	let position = 0; // loop position (the static column)
	let target = 0; // where offset.value is heading
	let moved = false; // the static column stays untouched until a button is pressed

	const animated = (): boolean => !isStill() && !reduceMotion.matches;
	let settled = (): void => undefined; // the column came to rest (set by the player below)

	const render = (): void => {
		if (!wrap) {
			return;
		}
		moved = true;
		gsap.set(items, { [axis]: wrap(position + offset.value) });
	};

	// The spring: offset.value pulled to target, velocity in px/s, on GSAP's ticker while it moves
	let velocity = 0;
	let springing = false;
	const tick = (_time: number, deltaTime: number): void => {
		const dt = Math.min(deltaTime, 50) / 1000; // a dropped frame (hidden tab) never throws it
		const steps = Math.ceil(dt / (1 / 240)); // semi-implicit Euler in small steps stays stable
		const h = dt / steps;
		for (let i = 0; i < steps; i++) {
			velocity += (-SPRING.stiffness * (offset.value - target) - SPRING.damping * velocity) * h;
			offset.value += velocity * h;
		}
		if (Math.abs(offset.value - target) < SPRING.rest && Math.abs(velocity) < SPRING.rest * 10) {
			offset.value = target;
			stopSpring();
			render();
			settled();
			return;
		}
		render();
	};
	const stopSpring = (): void => {
		velocity = 0;
		springing = false;
		gsap.ticker.remove(tick);
	};
	const spring = (): void => {
		if (!animated()) {
			stopSpring();
			offset.value = target;
			render();
			settled();
			return;
		}
		if (!springing) {
			springing = true;
			gsap.ticker.add(tick);
		}
	};

	const build = (): void => {
		const progress = setLength ? gsap.utils.wrap(0, 1, (position + offset.value) / setLength) : 0;
		stopSpring();

		axis = vertical.matches ? 'y' : 'x';
		gsap.set(items, { clearProps: 'transform' }); // drop the other axis' shift before measuring
		const style = getComputedStyle(track);
		const gap = parseFloat(axis === 'y' ? style.rowGap : style.columnGap) || 0;
		const box = videos[0].getBoundingClientRect();
		step = (axis === 'y' ? box.height : box.width) + gap;
		setLength = videos.length * step;
		if (!step) {
			return;
		}

		// At -set (the worst point of the loop either way) the copies must still reach the far edge
		const span = axis === 'y' ? track.clientHeight : track.clientWidth;
		while ((items.length / videos.length - 1) * setLength < span + gap) {
			for (const video of videos) {
				const copy = video.cloneNode(true) as HTMLElement;
				copy.setAttribute('aria-hidden', 'true');
				copy.removeAttribute('data-blog-video');
				copy.querySelectorAll('img').forEach((img) => (img.alt = ''));
				copy.querySelectorAll('button').forEach((button) => (button.tabIndex = -1));
				track.append(copy);
				items.push(copy);
			}
		}

		// (-set, 0] rather than gsap's [-set, 0): at rest on a multiple of set the real videos show, not their
		// aria-hidden copies (hung-hola showed the copies first, so the visible play button was out of the tab order)
		const inRange = gsap.utils.wrap(-setLength, 0);
		wrap = (value: number): number => {
			const shift = inRange(value);
			return shift <= -setLength + 0.5 ? shift + setLength : shift;
		};
		// Keep the phase when a breakpoint changes the video size, on a whole video: a rebuild in the middle of a
		// press (an image loading) must not leave the column resting between two videos
		position = -setLength + (Math.round(progress * videos.length) / videos.length) * setLength;
		offset.value = target = 0;
		if (moved || animated()) {
			render();
		}
	};

	const nudge = (direction: number): void => {
		if (!wrap) {
			return;
		}
		target += direction * step;
		spring();
	};

	// Player: YouTube's, built in a card on its first play (round 1 #21: nothing from YouTube loads before), one card at a
	// time. Once a card plays, clicks inside it go to YouTube's own controls (pause, sound, full screen).
	const command = (frame: HTMLIFrameElement, func: string): void =>
		frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func, args: [] }), 'https://www.youtube-nocookie.com');
	const pauseAll = (): void => track.querySelectorAll<HTMLIFrameElement>('.blog__media').forEach((frame) => command(frame, 'pauseVideo'));
	// With sound (owner 2026-10-22). A browser that blocks it (iOS Safari, unless the tap was inside the player) leaves
	// the player stopped: if it is not playing BLOCKED_AFTER ms after loading, it starts muted (YouTube's button turns the
	// sound on). The state comes from the iframe API's messages ("listening" asks for them).
	const states = new WeakMap<Window, number>(); // player window → YouTube's playerState (1 = playing)
	window.addEventListener('message', (event) => {
		if (event.origin !== 'https://www.youtube-nocookie.com' || !event.source || typeof event.data !== 'string') {
			return;
		}
		try {
			const state = JSON.parse(event.data)?.info?.playerState;
			if (typeof state === 'number') {
				states.set(event.source as Window, state);
			}
		} catch {
			// not the API's JSON
		}
	});
	const play = (card: HTMLElement, focus: boolean): void => {
		const id = card.dataset.blogYoutube;
		if (!id) {
			return;
		}
		pauseAll();
		const existing = card.querySelector<HTMLIFrameElement>('.blog__media');
		if (existing) {
			command(existing, 'playVideo');
			return;
		}
		const frame = document.createElement('iframe');
		const params = new URLSearchParams({ autoplay: '1', playsinline: '1', rel: '0', enablejsapi: '1', origin: location.origin });
		frame.src = `https://www.youtube-nocookie.com/embed/${id}?${params}`;
		frame.title = card.dataset.blogTitle || 'YouTube video';
		frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
		frame.allowFullscreen = true;
		frame.className = 'blog__media absolute inset-0 z-3 size-full border-0';
		frame.addEventListener('load', () => {
			frame.contentWindow?.postMessage(JSON.stringify({ event: 'listening', id }), 'https://www.youtube-nocookie.com');
			window.setTimeout(() => {
				const player = frame.contentWindow;
				if (player && states.get(player) !== 1 && frame.isConnected) {
					command(frame, 'mute');
					command(frame, 'playVideo');
				}
			}, BLOCKED_AFTER);
		});
		card.append(frame);
		card.classList.add('is-playing');
		if (focus) {
			frame.focus();
		}
	};
	track.addEventListener('click', (event) => {
		const card = (event.target as HTMLElement).closest<HTMLElement>('.blog__video');
		// The play button or anywhere on the card (round 1 #14); a drag's click is swallowed below
		if (card && !card.querySelector('.blog__media')) {
			play(card, true);
		}
	});
	// After an arrow (owner 2026-10-22): the video it brought in plays, with sound, once the column settles (focus stays
	// on the arrow)
	let playOnSettle = false;
	settled = (): void => {
		if (!playOnSettle) {
			return;
		}
		playOnSettle = false;
		const start = track.getBoundingClientRect()[axis === 'y' ? 'top' : 'left'];
		const shown = items.reduce((best, item) =>
			Math.abs(item.getBoundingClientRect()[axis === 'y' ? 'top' : 'left'] - start) < Math.abs(best.getBoundingClientRect()[axis === 'y' ? 'top' : 'left'] - start) ? item : best,
		);
		play(shown, false);
	};
	new IntersectionObserver(([entry]) => {
		if (!entry.isIntersecting) {
			pauseAll();
		}
	}).observe(track);

	// Drag: the column follows the pointer along its axis, then settles on a whole video
	const panel = track.closest<HTMLElement>('.blog__panel') ?? track;
	let drag: { id: number; start: number; from: number; last: number; time: number; speed: number; moved: boolean } | null = null;
	let dragged = false; // the last press was a drag: its click is swallowed
	const along = (event: PointerEvent): number => (axis === 'y' ? event.clientY : event.clientX);
	panel.addEventListener('pointerdown', (event) => {
		dragged = false;
		if (!wrap || !event.isPrimary || event.button !== 0 || (event.target as HTMLElement).closest('[data-blog-prev], [data-blog-next]')) {
			return;
		}
		stopSpring(); // caught mid-move: the column stops under the pointer
		playOnSettle = false;
		target = offset.value;
		const at = along(event);
		drag = { id: event.pointerId, start: at, from: offset.value, last: at, time: event.timeStamp, speed: 0, moved: false };
	});
	panel.addEventListener('pointermove', (event) => {
		if (!drag || event.pointerId !== drag.id) {
			return;
		}
		const at = along(event);
		if (!drag.moved) {
			if (Math.abs(at - drag.start) < DRAG_START) {
				return;
			}
			// Captured only now: a press that stays put is still a click on its play button
			drag.moved = dragged = true;
			panel.setPointerCapture(event.pointerId);
			panel.classList.add('is-dragging');
			pauseAll();
		}
		const elapsed = event.timeStamp - drag.time;
		if (elapsed > 0) {
			drag.speed = (at - drag.last) / elapsed;
		}
		drag.last = at;
		drag.time = event.timeStamp;
		offset.value = drag.from + at - drag.start;
		render();
	});
	const release = (event: PointerEvent): void => {
		if (!drag || event.pointerId !== drag.id) {
			return;
		}
		const { moved: wasDrag, speed, time } = drag;
		drag = null;
		if (!wasDrag || !step) {
			return;
		}
		panel.classList.remove('is-dragging');
		const fresh = event.timeStamp - time < 100; // a pointer held still before the release throws nothing
		target = Math.round((offset.value + (fresh ? speed * THROW : 0)) / step) * step;
		velocity = fresh ? speed * 1000 : 0; // px/ms → px/s: the spring starts at the hand's speed
		spring();
	};
	panel.addEventListener('pointerup', release);
	panel.addEventListener('pointercancel', release);
	panel.addEventListener(
		'click',
		(event) => {
			if (dragged) {
				event.preventDefault();
				event.stopPropagation();
				dragged = false;
			}
		},
		true,
	);
	panel.addEventListener('dragstart', (event) => event.preventDefault());

	prev?.addEventListener('click', () => {
		pauseAll();
		playOnSettle = true;
		nudge(1);
	});
	next?.addEventListener('click', () => {
		pauseAll();
		playOnSettle = true;
		nudge(-1);
	});

	// Rebuild when the column or the videos change size (breakpoint, posters loading)
	let frame = 0;
	const observer = new ResizeObserver(() => {
		cancelAnimationFrame(frame);
		frame = requestAnimationFrame(build);
	});
	observer.observe(track);
	observer.observe(videos[0]);
	reduceMotion.addEventListener('change', build);
	vertical.addEventListener('change', build);
});
